import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    // TL OVERRIDE: widen look-ahead so recommendations fetch before they are reached
    { rootMargin: '0px 0px 1000px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Whether a fetch is currently loading recommendations.
   * @type {boolean}
   */
  #isLoading = false;

  /**
   * Cancels the idle preload when the element disconnects.
   * @type {(() => void) | null}
   */
  #cancelIdleLoad = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.loadTiming === 'idle') {
      this.#scheduleIdleLoad();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#cancelIdleLoad?.();
    this.#cancelIdleLoad = null;
    // TL OVERRIDE: abort any in-flight fetch so it can't setState after disconnect
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true' || this.#isLoading) {
      return;
    }

    this.#isLoading = true;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.#prepareRecommendationImages(recommendations);
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        // TL OVERRIDE: an aborted fetch (restart or disconnect) is intentional, not an error
        if (e.name !== 'AbortError') this.#handleError(e);
      })
      .finally(() => {
        this.#isLoading = false;
      });
  }

  #scheduleIdleLoad() {
    if (this.#cancelIdleLoad) return;

    const load = () => {
      this.#cancelIdleLoad = null;
      this.#loadRecommendations();
    };

    if ('requestIdleCallback' in window) {
      const idleCallback = /** @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number} */ (
        window.requestIdleCallback
      );
      const cancelIdleCallback = /** @type {(handle: number) => void} */ (window.cancelIdleCallback);
      const handle = idleCallback(load, { timeout: 2200 });
      this.#cancelIdleLoad = () => cancelIdleCallback(handle);
      return;
    }

    const handle = window.setTimeout(load, 1200);
    this.#cancelIdleLoad = () => window.clearTimeout(handle);
  }

  /**
   * @param {Element} recommendations
   */
  #prepareRecommendationImages(recommendations) {
    const { loadedImageLoading, loadedImageFetchpriority, eagerImageFetchpriority } = this.dataset;

    if (!loadedImageLoading && !loadedImageFetchpriority && !this.dataset.eagerCount) return;

    // TL OVERRIDE: promote the first row to eager so it is ready the moment the rail is fetched ahead of the viewport
    const eagerCount = parseInt(this.dataset.eagerCount) || 0;
    let index = 0;
    for (const image of recommendations.querySelectorAll('img.product-media__image')) {
      if (index < eagerCount) {
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', eagerImageFetchpriority || 'low');
      } else {
        if (loadedImageLoading) {
          image.setAttribute('loading', loadedImageLoading);
        }

        if (loadedImageFetchpriority) {
          image.setAttribute('fetchpriority', loadedImageFetchpriority);
        }
      }
      index++;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
